<template>
  <side-modal
    :show="show"
    @update:show="(val) => $emit('update:show', val)"
    location="left"
    close-on-overlay-click
    absolute
    width="576px"
    close-on-focus-out
  >
    <div class="events-modal-content">
      <div class="header d-flex justify-space-between p-3 align-center">
        <h5 class="title" id="offcanvas-events-label">
          <strong>Events</strong>
        </h5>
        <v-btn icon size="small" variant="text" @click="$emit('update:show', false)">
          <v-icon size="xx-large" color="black">mdi-close</v-icon></v-btn
        >
      </div>
      <div class="body">
        <section class="events-search">
          <input
            type="text"
            class="form-control"
            v-model="eventsSearchInput"
            placeholder="Search"
          />
        </section>

        <section class="events-filters">
          <div
            class="btn-group"
            id="resultsView"
            role="group"
            aria-label="View results as cards or as a list"
          >
            <input
              type="radio"
              class="btn-check"
              id="EventsViewCards"
              autocomplete="off"
              value="cards"
              v-model="resultsView"
              :checked="resultsView === 'cards'"
            />
            <label
              class="btn"
              for="EventsViewCards"
              id="EventsViewCardslabel"
              @click="resultsView = 'cards'"
              ><i class="bi bi-grid"></i
            ></label>

            <input
              type="radio"
              class="btn-check"
              id="EventsViewList"
              autocomplete="off"
              value="list"
              v-model="resultsView"
              :checked="resultsView === 'list'"
              @update="resultsView = 'list'"
            />
            <label
              class="btn"
              for="EventsViewList"
              id="EventsViewListlabel"
              @click="resultsView = 'list'"
              ><i class="bi bi-list-ul"></i
            ></label>
          </div>

          <div class="btn-group" id="resultsSort" role="group" aria-label="Sort results">
            <input
              type="radio"
              class="btn-check"
              id="eventSortName"
              autocomplete="off"
              :checked="!archived"
              value="Name"
            />
            <label
              @click="archived = false"
              class="btn"
              for="eventSortName"
              id="eventSortNameLabel"
            >
              <span>Current</span>
            </label>

            <input
              type="radio"
              class="btn-check"
              id="eventSortDescription"
              autocomplete="off"
              value="Description"
              :checked="archived"
            />
            <label
              class="btn"
              for="eventSortDescription"
              id="eventSortDescriptionLabel"
              @click="archived = true"
            >
              <span>Archived</span>
            </label>
          </div>
        </section>

        <section :class="['events-results', resultsView + '-view']" :view-as="resultsView">
          <div class="list-headers">
            <span class="meta" col="event-name">Event Name</span>
            <span class="meta" col="teacher">Teacher/Team</span>
          </div>
          <div :class="['event-row', resultsView + '-view']">
            <div
              class="col event"
              v-for="(event, index) in filtered"
              :key="index"
              @click="
                conferenceStore.setSelected(event);
                $emit('update:show', false);
              "
            >
              <label
                :class="[
                  'btn-card',
                  {
                    selected:
                      event.conference_id == conferenceStore.selected?.conference_id &&
                      event.team == conferenceStore.selected?.team
                  }
                ]"
                :for="event.conference_id + ''"
                role="button"
                tabindex="1"
              >
                <div class="card-body">
                  <div class="meta" col="event-name">
                    <span>{{ event.conference_name }}</span>
                  </div>
                  <div class="meta" col="teacher">
                    <div class="d-flex gap-2">
                      <i
                        v-if="'team' in event"
                        class="mdi mdi-account-multiple type-icon"
                        title="Description"
                        alt="Description"
                      ></i>
                      <i
                        v-else
                        class="mdi mdi-account type-icon"
                        title="Description"
                        alt="Description"
                      ></i>
                      <span>{{
                        event.team?.team_name ??
                        authStore.me.teacher_fname + " " + authStore.me.teacher_lname
                      }}</span>
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>
          <template v-if="!filtered.length && conferenceStore.loading">
            <loading-state text="Loading Events"></loading-state>
          </template>
        </section>
      </div>
    </div>
  </side-modal>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/stores/authentication";
import { useConferenceStore } from "@/stores/teacher/conference";
import { computed, ref } from "vue";
import sideModal from "../sideModal.vue";
import LoadingState from "../loadingState.vue";

defineProps<{ show: boolean }>();
defineEmits<{
  (e: 'update:show', ...args: any[]): void
}>();

const conferenceStore = useConferenceStore();
const authStore = useAuthenticationStore();

const eventsSearchInput = ref("");
const archived = ref(false);

// By default, show teacher results as cards.
const resultsView = ref("cards");

const filtered = computed(() =>
  conferenceStore[archived.value ? "archived_conferences" : "current_conferences"].filter((i) =>
    (
      i.conference_name +
      (i.team?.team_name ?? authStore.me.teacher_fname + " " + authStore.me.teacher_lname)
    )
      .toLowerCase()
      .includes(eventsSearchInput.value.toLowerCase())
  )
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

$font-size-sm: 1rem;

.events-modal-content {
  height: 100%;
  background-color: $primary-25;
}

.header {
  .title {
    color: $primary;
  }
}

.body {
  padding: 1rem;
  display: flex;
  gap: 1.5rem;
  flex-direction: column;

  section {
    &.events-search {
      padding-bottom: 1rem !important ;
    }

    &.events-filters {
      display: flex;
      justify-content: space-between;

      .btn-group {
        gap: 3px;
        padding: 3px;
        background: $gray-200;
        border-radius: $border-radius-lg;
        .btn {
          border-radius: $border-radius;
          background: $gray-200;
          margin: 0;
          padding: 0.125rem 0.625rem;
          min-width: 2rem;
          text-transform: capitalize;
          letter-spacing: normal;
          font-size: $font-size-sm;
          font-weight: $font-weight-normal;
          align-self: stretch;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.125rem;
          .bi {
            font-size: 1rem;
            line-height: 1;
            width: 1rem;
            height: 1rem;
            &[asc] {
              font-size: 0.75rem;
              line-height: inherit;
              transform-origin: center;
              margin-right: -0.125rem;
              &[asc="false"] {
                transform: rotate(180deg);
              }
            }
          }
        }
        .btn-check:checked + .btn {
          background: $white;
          box-shadow: none;
          color: $gray-800;
          font-weight: $font-weight-semibold;
          &[ascending] {
            &::after {
              transition: transform 0.1s ease-out;
              display: inline-block;
              content: "";
              background-repeat: no-repeat;
              font-size: 0.75rem;
              width: 1em;
              height: 1em;
              margin: 0 -1px 0 2px;
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='#{encodecolor($gray-600)}' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
            }
            &[ascending="false"]::after {
              transform: rotate(180deg);
            }
          }
        }
        span {
          font-size: 0.875rem;
        }
      }
    }

    &.events-results {
      flex: 1;
      overflow: auto;

      .list-headers {
        display: none;
      }

      > .event-row {
        gap: 10px;
        width: 100%;
        display: flex;
        flex-wrap: wrap;

        .col.event {
          flex-basis: calc(50% - 5px);

          .btn-card {
            width: 100%;
            border-radius: 4px;
            border: 1px solid #eadfd8;
            background-color: #fff;
            .card-body {
              width: 100%;
              padding: 10px;
              .type-icon {
                color: $gray-400;
              }
            }
            &.selected {
              background-color: $secondary;
              color: white;
              .type-icon {
                color: white;
              }
            }
          }
        }
      }
      &.list-view {
        [col] {
          width: 25%;
          padding: 0;
          flex-wrap: wrap;
          &[col="event-name"] {
            flex-grow: 1;
          }
        }
        .list-headers {
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #695b59;
          font-size: 0.75rem;
          display: flex;
        }
        > .event-row {
          .col.event {
            flex-basis: 100%;
          }
          .card-body {
            display: flex;
          }
        }
      }
    }
  }
}
</style>
